/**
 * kCp1252Core - windows-1252 bytes -> a string, by its own table, independent of the runtime's TextDecoder.
 *
 * WHY IT EXISTS (measured 2026-10-17): Node v22.20.0 on Linux (Ubuntu 24.04, ICU 77.1) answers
 * `new TextDecoder("windows-1252").decode([0x80, 0x93, 0x96])` with U+0080 U+0093 U+0096 - ISO-8859-1 wearing
 * the windows-1252 label (`.encoding` still says "windows-1252") - while Node v22.22.2 on Windows answers
 * U+20AC U+201C U+2013. Every curly quote, dash and euro sign in a western mail or an Excel CSV became a C1
 * control character. A decoder that depends on the runtime cannot be trusted for this label, so this one does not.
 *
 * The table is the WHATWG Encoding Standard's index-windows-1252 for 0x80-0x9F; 0x00-0x7F and 0xA0-0xFF are
 * the same code point as the byte. The five bytes the index leaves undefined (0x81 0x8D 0x8F 0x90 0x9D) map to
 * the same code point, exactly as the WHATWG decoder does. Pure: safe in a browser, Node and an Azure Function.
 */
import { getCoreUtils } from "./kCoreErrorRegistry";

export class kCp1252Core {
    /** Code points for bytes 0x80-0x9F (index = byte - 0x80). */
    public static readonly High: number[] = [
        0x20AC, 0x0081, 0x201A, 0x0192, 0x201E, 0x2026, 0x2020, 0x2021, 0x02C6, 0x2030, 0x0160, 0x2039, 0x0152, 0x008D, 0x017D, 0x008F,
        0x0090, 0x2018, 0x2019, 0x201C, 0x201D, 0x2022, 0x2013, 0x2014, 0x02DC, 0x2122, 0x0161, 0x203A, 0x0153, 0x009D, 0x017E, 0x0178
    ];

    /** The code point one byte stands for. DOCUMENTED EXCEPTION: pure deterministic helper; its caller is gated. */
    public static CodePoint = (Byte: number): number => {
        if (Byte >= 0x80 && Byte <= 0x9F) { return kCp1252Core.High[Byte - 0x80]; }
        return Byte;
    };

    /** windows-1252 bytes -> text. Every byte has a character, so only a fault in this code can reach the catch. */
    public static Decode = (Bytes: Uint8Array): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Parts: string[] = [];
            const Chunk: number = 8192;
            for (let Start: number = 0; Start < Bytes.length; Start = Start + Chunk) {
                const End: number = Math.min(Bytes.length, Start + Chunk);
                const Codes: number[] = new Array<number>(End - Start);
                for (let i: number = Start; i < End; i++) { Codes[i - Start] = kCp1252Core.CodePoint(Bytes[i]); }
                Parts.push(String.fromCharCode.apply(null, Codes));
            }
            return Parts.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kCp1252Core.Decode");
            return "";
        }
    };
}
